import { useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { Tag } from '@/types/entities'
import { useToast } from '@/hooks/use-toast'

interface TagSelectProps {
  value: number[]
  onChange: (value: number[]) => void
  tags: Tag[]
  maxTags?: number
}

export function TagSelect({ value, onChange, tags, maxTags = 5 }: TagSelectProps) {
  const [open, setOpen] = useState(false)
  const { toast } = useToast()

  const availableTags = tags.filter((t) => !value.includes(t.id))

  const handleSelect = (tagId: number) => {
    if (value.length >= maxTags) {
      toast({
        title: 'Tag limit reached',
        description: `You can only select up to ${maxTags} tags`,
        variant: 'destructive',
      })
      return
    }

    const newValue = [...value, tagId]
    onChange(newValue)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between"
        >
          Add tag
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput placeholder="Search tags..." />
          <CommandEmpty>No tag found.</CommandEmpty>
          <CommandGroup>
            {availableTags.map((tag) => (
              <CommandItem
                key={tag.id}
                value={tag.name}
                onSelect={() => handleSelect(tag.id)}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    value.includes(tag.id) ? 'opacity-100' : 'opacity-0'
                  )}
                />
                {tag.name}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

